import { useEffect } from "react";

import Spinner from "../../../shared/ui/Loading/Spinner";

// ClientShareRevokeConfirmModal — feature-local T-6 section-local
// destructive confirmation widget for revoking a share row.
//
// Closed-class reference: architecture-freeze-r8.md §4 T-6
// ("section-local destructive: inline confirmation + DELETE +
// section refetch. No optimistic eviction, no cross-section
// coordination").
//
// Topology class — distinct from the T-4 delete-client workflow
// (sibling [[ClientDeleteConfirmModal]]):
//   * T-4 destroys the parent resource ⇒ navigation IS the
//     successor state.
//   * T-6 destroys a row inside a section ⇒ the section continues
//     to exist; success path is a section-local refetch.
//
// I6 §"Q4 procedural test" compliance — this widget is local iff:
//   1. Signature names no entity, endpoint, resource, or workflow.
//      (Props: open / recipientName / revoking / errorMessage /
//      onCancel / onConfirm — copy is the section's responsibility
//      via recipientName; no entityType / endpoint / resourceKey.)
//   2. Body inlines the confirmation copy (no config schema, no
//      items prop, no template strings).
//   3. Consumed by exactly ONE feature ([[ClientSharesCard]]).
//   4. Deleting this file affects exactly ONE feature.
//   5. Passes none of D-T1/D-T2/D-T5/D-T6 — no entity prop, no
//      shared consumers, no cascade pre-flight, no undo affordance.
//
// I6-proposed §6.5 (A-2) compliance:
//   1. Feature-local widget; signature names no entity/endpoint.
//   2. Success path is section-local refetch — NOT navigation.
//      (The sidebar card stays mounted; the parent route stays
//      mounted; only this row's existence flips to "no longer
//      present" once the section's refetch resolves.)
//   3. No mayBeStale is emitted at all — this is section-local,
//      not cross-screen. R6-15 explicitly: no cross-surface
//      eviction; no list invalidation; no broadcast.
//   4. No optimistic collection patching — the row remains visible
//      until the DELETE 204 returns and the section's refetch lands.
//   5. No cascade pre-flight — the backend cascade is one row in
//      one table (ClientShare CASCADE on Client and on User; no
//      dependent rows of its own). No counts pre-fetched.
//   6. No event semantics. No WS push. No notification taxonomy
//      (B2 F-L verified: backend has no pre_delete/post_delete on
//      ClientShare; the recipient's discovery vector is their own
//      next request returning 404. The owner sees no system
//      acknowledgement either — the disappearance from the list IS
//      the acknowledgement).
//   7. No undo affordance. The confirmation gate is the prevention
//      (I6 closing posture).
//   8. No pre-emptive permission eviction. Owner gating happens
//      one layer up — [[ClientSharesCard]] only renders when
//      client.is_owner is true (mirrors backend IsClientOwnerOnly).
//
// Allowed UI-local effects per CLAUDE.md "Presentation Primitive
// Rules": ESC-key handler + body scroll lock. No selectors, no
// dispatch, no imperative handle, no registry, no portal, no
// shared modal host. Reuses the feature-local modal CSS vocabulary
// established by S4's ClientEditModal — the modal shell is the
// same; the destructive language is expressed locally via the
// __action-btn--danger modifier and the inlined warning copy.

function ClientShareRevokeConfirmModal({
  open,
  recipientName,
  revoking,
  errorMessage,
  onCancel,
  onConfirm,
}) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !revoking) {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, revoking, onCancel]);

  if (!open) return null;

  const safeName = (recipientName || "").trim() || "цього рієлтора";

  return (
    <div
      className="feature-client-detail__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-share-revoke-modal-title"
    >
      <div
        className="feature-client-detail__modal-backdrop"
        aria-hidden="true"
        onClick={revoking ? undefined : onCancel}
      />
      <div className="feature-client-detail__modal-dialog feature-client-detail__modal-dialog--sm">
        <header className="feature-client-detail__modal-head">
          <h2
            id="client-share-revoke-modal-title"
            className="feature-client-detail__modal-title"
          >
            <i className="fa-solid fa-share-nodes" aria-hidden="true" />
            <span>Відкликати доступ</span>
          </h2>
          <button
            type="button"
            className="feature-client-detail__modal-close"
            onClick={onCancel}
            disabled={revoking}
            aria-label="Закрити вікно підтвердження"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-client-detail__modal-body">
          <p className="feature-client-detail__delete-question">
            Відкликати доступ у <strong>{safeName}</strong>?
          </p>
          <p className="feature-client-detail__delete-cascade">
            <i
              className="fa-solid fa-circle-info"
              aria-hidden="true"
            />
            <span>
              Рієлтор втратить доступ до цього клієнта. Щоб поновити доступ,
              потрібно буде поділитися знову.
            </span>
          </p>
          {errorMessage ? (
            <p
              className="feature-client-detail__delete-error"
              role="alert"
            >
              {errorMessage}
            </p>
          ) : null}
        </div>
        <footer className="feature-client-detail__modal-foot">
          <button
            type="button"
            className="feature-client-detail__action-btn feature-client-detail__action-btn--ghost"
            onClick={onCancel}
            disabled={revoking}
          >
            Скасувати
          </button>
          <button
            type="button"
            className="feature-client-detail__action-btn feature-client-detail__action-btn--danger"
            onClick={onConfirm}
            disabled={revoking}
            data-loading={revoking ? "true" : undefined}
          >
            {revoking ? (
              <Spinner size="sm" label="Відкликання" />
            ) : (
              <>
                <i className="fa-solid fa-xmark" aria-hidden="true" />
                <span>Відкликати</span>
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default ClientShareRevokeConfirmModal;
